<template>
<div class="wn-toolbar">
    <div class="wn-toolbar__heading">
        <span class="wn-toolbar__title">{{ title }}</span>
        <span v-if="caption" class="wn-toolbar__caption">{{ caption }}</span>
    </div>

    <div class="wn-toolbar__search" :class="{ 'wn-toolbar__search--collapsible': searchCollapsible }">
        <div v-if="searchCollapsible" class="wn-search-toggle" :class="{ 'is-open': searchOpen || search }">
            <button type="button" class="wn-search-toggle__icon" :aria-label="$_locale('search')" @click="openSearch"><i class="ri-search-line"></i></button>
            <input v-if="searchOpen || search" ref="searchInput" type="text" class="form-control wn-search-toggle__input" :value="search" :placeholder="placeholder"
                @input="$emit('update:search', $event.target.value)" @blur="searchOpen = false">
        </div>
        <label v-else class="wn-search">
            <i class="ri-search-line"></i>
            <input type="search" :value="search" :placeholder="placeholder" @input="$emit('update:search', $event.target.value)">
            <button v-if="search" type="button" class="wn-search__clear" :title="$_locale('clear')" @click="$emit('update:search', '')"><i class="ri-close-line"></i></button>
        </label>
        <wn-filter-trigger v-if="filters.length" :count="filterCount" :pressed="filterOpen" @click="filterOpen = true" @clear="$emit('update:filterValue', {})"></wn-filter-trigger>
    </div>

    <div class="wn-toolbar__actions">
        <slot name="actions"></slot>
        <div v-if="columns.length" class="wn-menu-wrap">
            <button type="button" class="wn-icon-btn" :title="$_locale('show columns')" aria-haspopup="menu" :aria-expanded="menu === 'columns'" @click="toggle('columns')"><i class="ri-more-2-fill"></i></button>
            <div v-if="menu === 'columns'" class="wn-menu wn-menu--end" role="menu">
                <span class="wn-menu__heading">{{ $_locale('show columns') }}</span>
                <label v-for="c in columns" :key="c.key" class="wn-menu__item">
                    <input type="checkbox" class="form-check-input" :checked="!hidden.includes(c.key)" :disabled="c.locked" @change="toggleColumn(c.key)">
                    {{ c.label }}
                </label>
                <button type="button" class="wn-menu__item wn-menu__item--link" @click="restoreDefaults">{{ $_locale('restore defaults') }}</button>
            </div>
        </div>
    </div>
    <div v-if="menu" class="wn-menu-backdrop" @click="menu = null"></div>

    <div v-if="selectedCount > 0 && $slots.selection" class="wn-selection-bar" role="region"
        :style="{ left: barLeft }" :aria-label="`${ selectedCount } ${ $_locale('selected', false) }`">
        <span class="wn-toolbar__check"><i class="ri-check-line"></i></span>
        <span class="wn-selection-bar__title">{{ selectedCount }} {{ $_locale('selected', false) }}</span>
        <div class="wn-menu-wrap">
            <button type="button" class="wn-btn wn-btn--inverse" aria-haspopup="menu" :aria-expanded="menu === 'actions'" @click="toggle('actions')">
                {{ $_locale('actions') }}<i class="ri-arrow-down-s-line"></i>
            </button>
            <div v-if="menu === 'actions'" class="wn-menu wn-menu--up" role="menu" @click="menu = null"><slot name="selection"></slot></div>
        </div>
        <button type="button" class="wn-icon-btn" :title="$_locale('clear selection')" @click="$emit('clear-selection')"><i class="ri-close-line"></i></button>
    </div>

    <wn-filter-panel v-if="filters.length" :open="filterOpen" :groups="filters" :value="filterValue"
        @apply="$emit('update:filterValue', $event)" @close="filterOpen = false"></wn-filter-panel>
</div>
</template>

<script>
import WnFilterTrigger from './WnFilterTrigger.vue'
import WnFilterPanel from './WnFilterPanel.vue'
import { countActive } from './listFilters.js'

export default {
    name: 'WnTableToolbar',
    components: { WnFilterTrigger, WnFilterPanel },
    props: {
        title: { type: String, default: '' },
        caption: { type: String, default: '' },
        placeholder: { type: String, default: '' },
        search: { type: String, default: '' },
        // search starts as a round icon button that reveals the input (same look as the automation list)
        searchCollapsible: { type: Boolean, default: false },
        // WnFilterPanel groups + applied value
        filters: { type: Array, default: () => [] },
        filterValue: { type: Object, default: () => ({}) },
        selectedCount: { type: Number, default: 0 },
        // column menu: columns [{ key, label, locked? }], hidden = keys currently hidden
        columns: { type: Array, default: () => [] },
        hidden: { type: Array, default: () => [] },
        defaultHidden: { type: Array, default: () => [] },
        // CSS selector prefix scoping the generated hide-rule to one table, e.g. '#shareListApp'
        parent: { type: String, default: '' },
    },
    emits: ['update:search', 'update:filterValue', 'update:hidden', 'clear-selection', 'restore-defaults'],
    data: () => ({ searchOpen: false, menu: null, filterOpen: false, styleEl: null, barLeft: '50%', resizeObserver: null }),
    computed: {
        filterCount() { return countActive(this.filters, this.filterValue); },
    },
    watch: {
        hidden() { this.generateCSS(); },
    },
    methods: {
        // the selection bar floats over the viewport but is centered on the list card, not the whole window
        measureBar() {
            const target = this.$el.closest('.wn-list') || this.$el;
            const rect = target.getBoundingClientRect();

            this.barLeft = `${ rect.left + rect.width / 2 }px`;
        },
        openSearch() {
            this.searchOpen = true;
            this.$nextTick(() => this.$refs.searchInput?.focus());
        },
        toggle(name) { this.menu = this.menu === name ? null : name; },
        // hidden columns reset here; views with other column state (e.g. the order) listen to restore-defaults
        restoreDefaults() {
            this.$emit('update:hidden', [...this.defaultHidden]);
            this.$emit('restore-defaults');
        },
        toggleColumn(key) {
            this.$emit('update:hidden', this.hidden.includes(key) ? this.hidden.filter((k) => k !== key) : [...this.hidden, key]);
        },
        // generated by claude - same trick as WnTableColvis: hide columns via an injected
        // <style> rule (td[col]/th[col] display:none) instead of v-if, so a column's cells
        // never need to be toggled in every head/body/footer row by hand.
        generateCSS() {
            const scope = this.parent ? `${ this.parent } ` : '';
            const selectors = this.hidden.map((key) => `${ scope }td[col="${ key }"], ${ scope }th[col="${ key }"]`);

            this.styleEl.innerHTML = selectors.length ? `${ selectors.join(', ') } { display: none; }` : '';
        },
    },
    mounted() {
        const style = document.createElement('style');

        document.body.appendChild(style);
        this.styleEl = style;
        this.generateCSS();

        this.measureBar();
        this.resizeObserver = new ResizeObserver(() => this.measureBar());
        this.resizeObserver.observe(this.$el.closest('.wn-list') || this.$el);
    },
    beforeUnmount() {
        this.styleEl?.remove();
        this.resizeObserver?.disconnect();
    },
}
</script>
